import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import Header from './Header';
import Footer from './Footer';
import Seo from './Seo';
import { useUser } from '../contexts/UserContext';
import './BlogPost.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';

// Slugifies a heading for use as an anchor id. Same rules as the backend's
// slugify so admin-typed slugs and auto-anchored TOC entries align.
function headingSlug(text) {
  return (text || '').toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

// Extract H2/H3 headings from raw markdown for the sticky TOC. Cheap and
// good enough — we don't try to parse code-blocks for false-positive '##'.
function extractToc(md) {
  if (!md) return [];
  const out = [];
  let inCode = false;
  for (const line of md.split('\n')) {
    if (line.trim().startsWith('```')) { inCode = !inCode; continue; }
    if (inCode) continue;
    const m = /^(#{2,3})\s+(.+)$/.exec(line);
    if (m) out.push({ level: m[1].length, text: m[2].trim(), id: headingSlug(m[2]) });
  }
  return out;
}

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return ''; }
}

function PostCodeBlock({ language, value }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="terview-code-block">
      <div className="terview-code-header">
        <span className="terview-code-lang">{language || 'code'}</span>
        <button
          type="button"
          className="terview-code-copy"
          onClick={() => {
            try {
              navigator.clipboard.writeText(value);
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            } catch { /* clipboard blocked */ }
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <SyntaxHighlighter
        language={language || 'text'}
        style={oneDark}
        showLineNumbers
        wrapLongLines
        customStyle={{
          margin: 0,
          padding: '14px 16px',
          background: '#0d1117',
          fontSize: '0.88em',
          borderBottomLeftRadius: 8,
          borderBottomRightRadius: 8,
        }}
        codeTagProps={{ style: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' } }}
      >
        {value.replace(/\n$/, '')}
      </SyntaxHighlighter>
    </div>
  );
}

// Markdown component overrides: heading anchors for TOC linking, code blocks
// routed through SyntaxHighlighter (same look as the interview pane).
function makeMdComponents() {
  const headingRenderer = (Tag) => ({ children }) => {
    const text = React.Children.toArray(children).map(c =>
      typeof c === 'string' ? c : (c.props?.children || '')
    ).join('');
    const id = headingSlug(text);
    return <Tag id={id} className="blog-post-h"><a href={`#${id}`} className="blog-post-h-anchor">{children}</a></Tag>;
  };
  return {
    h1: headingRenderer('h1'),
    h2: headingRenderer('h2'),
    h3: headingRenderer('h3'),
    code({ inline, className, children }) {
      const match = /language-(\w+)/.exec(className || '');
      const value = String(children ?? '');
      if (!inline && (match || value.includes('\n'))) {
        return <PostCodeBlock language={match ? match[1] : ''} value={value} />;
      }
      return <code className="terview-inline-code">{children}</code>;
    },
    a({ href, children }) {
      const external = href && /^https?:\/\//.test(href);
      return external
        ? <a href={href} target="_blank" rel="noopener noreferrer" className="blog-post-link">{children}</a>
        : <a href={href} className="blog-post-link">{children}</a>;
    },
  };
}

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useUser();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeId, setActiveId] = useState('');
  const articleRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    const token = localStorage.getItem('token');
    fetch(`${API_BASE_URL}/api/blog/${encodeURIComponent(slug)}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(async r => {
        if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Not found');
        return r.json();
      })
      .then(data => { if (!cancelled) setPost(data.post); })
      .catch(e => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  const toc = useMemo(() => extractToc(post?.content_md), [post]);
  const mdComponents = useMemo(() => makeMdComponents(), []);

  // Active-section highlighting in the TOC.
  useEffect(() => {
    if (!articleRef.current || toc.length === 0) return;
    const headings = Array.from(articleRef.current.querySelectorAll('h1[id], h2[id], h3[id]'));
    if (headings.length === 0) return;
    const obs = new IntersectionObserver(
      entries => {
        const visible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: '-80px 0px -70% 0px', threshold: 0 }
    );
    headings.forEach(h => obs.observe(h));
    return () => obs.disconnect();
  }, [toc, post]);

  if (loading) {
    return (
      <div className="blog-post-page">
        <Header />
        <div className="blog-post-loading">Loading…</div>
        <Footer />
      </div>
    );
  }
  if (error || !post) {
    return (
      <div className="blog-post-page">
        <Header />
        <div className="blog-post-error">
          <h2>Post not found</h2>
          <p>{error || 'This article may have been moved or unpublished.'}</p>
          <Link to="/blog" className="blog-post-back-link">← Back to blog</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="blog-post-page">
      <Seo
        title={post.title}
        description={post.summary || post.title}
        path={`/blog/${post.slug || slug}`}
        image={post.hero_image_url || undefined}
        noindex={post.status === 'draft'}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: post.title,
          description: post.summary || undefined,
          image: post.hero_image_url || undefined,
          datePublished: post.published_at || post.created_at || undefined,
          dateModified: post.updated_at || undefined,
          author: { '@type': 'Person', name: post.author_name || 'Terview AI' },
          publisher: {
            '@type': 'Organization',
            name: 'Terview AI',
            logo: {
              '@type': 'ImageObject',
              url: 'https://terview.ai/android-chrome-512x512.png',
            },
          },
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `https://terview.ai/blog/${post.slug || slug}`,
          },
        }}
      />
      <Header />

      <div className="blog-post-container">
        <nav className="blog-breadcrumbs">
          <Link to="/">Home</Link>
          <span>›</span>
          <Link to="/blog">Blog</Link>
          <span>›</span>
          <span className="blog-breadcrumbs-current">{post.title}</span>
        </nav>

        {/* Hero card */}
        <div className={`blog-post-hero glass ${post.hero_image_url ? '' : 'no-image'}`}>
          {post.hero_image_url && (
            <div className="blog-post-hero-img">
              <img src={post.hero_image_url} alt={post.title} />
            </div>
          )}
          <div className="blog-post-hero-text">
            {post.status === 'draft' && (
              <span className="blog-post-draft-tag">DRAFT — only admins can see this</span>
            )}
            <h1 className="blog-post-title">{post.title}</h1>
            {post.summary && <p className="blog-post-summary">{post.summary}</p>}
            <div className="blog-post-meta-row">
              <div className="blog-post-meta-block">
                <div className="blog-post-meta-label">WRITTEN BY</div>
                <div className="blog-post-meta-author">
                  <span className="blog-avatar-fallback" style={{ width: 28, height: 28, fontSize: 12, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', background: 'linear-gradient(135deg, #6d4f9f, #4a7bb1)', color: '#fff', fontWeight: 700, marginRight: 8 }}>
                    {(post.author_name || '?').charAt(0).toUpperCase()}
                  </span>
                  {post.author_name}
                </div>
              </div>
              <div className="blog-post-meta-block">
                <div className="blog-post-meta-label">UPDATED ON</div>
                <div className="blog-post-meta-author">
                  {formatDate(post.published_at || post.updated_at)} · {post.read_minutes || 1} min read
                </div>
              </div>
            </div>

            {/* Social share */}
            <div className="blog-share-row">
              <span className="blog-share-label">Share</span>
              <div className="blog-share-btns">
                <a className="blog-share-btn sh-x" target="_blank" rel="noopener noreferrer" aria-label="Share on X" title="Share on X"
                   href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(post.title)}`}>
                  <svg viewBox="0 0 24 24" width="17" height="17" fill="currentColor" aria-hidden="true"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                </a>
                <a className="blog-share-btn sh-li" target="_blank" rel="noopener noreferrer" aria-label="Share on LinkedIn" title="Share on LinkedIn"
                   href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}>
                  <svg viewBox="0 0 24 24" width="17" height="17" fill="currentColor" aria-hidden="true"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                </a>
                <a className="blog-share-btn sh-fb" target="_blank" rel="noopener noreferrer" aria-label="Share on Facebook" title="Share on Facebook"
                   href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}>
                  <svg viewBox="0 0 24 24" width="17" height="17" fill="currentColor" aria-hidden="true"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </a>
                <a className="blog-share-btn sh-wa" target="_blank" rel="noopener noreferrer" aria-label="Share on WhatsApp" title="Share on WhatsApp"
                   href={`https://wa.me/?text=${encodeURIComponent(post.title + ' ' + window.location.href)}`}>
                  <svg viewBox="0 0 24 24" width="17" height="17" fill="currentColor" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.885-9.885 9.885M20.52 3.449C18.24 1.245 15.24 0 12.045 0 5.463 0 .104 5.359.101 11.892c0 2.096.546 4.142 1.588 5.945L0 24l6.335-1.652a11.882 11.882 0 005.71 1.454h.006c6.585 0 11.946-5.359 11.949-11.945a11.821 11.821 0 00-3.48-8.408"/></svg>
                </a>
                <button type="button" className="blog-share-btn sh-copy" aria-label="Copy link" title="Copy link"
                   onClick={e => {
                     navigator.clipboard?.writeText(window.location.href);
                     const b = e.currentTarget; b.classList.add('copied');
                     setTimeout(() => b.classList.remove('copied'), 1500);
                   }}>
                  <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>
                </button>
              </div>
            </div>

            {user?.is_admin && (
              <div className="blog-post-admin-actions">
                <button
                  type="button"
                  className="blog-post-admin-btn"
                  onClick={() => navigate(`/blog/admin/edit/${post.id}`)}
                >
                  ✎ Edit
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Body: TOC + content */}
        <div className="blog-post-body">
          {toc.length > 0 && (
            <aside className="blog-post-toc glass">
              <div className="blog-post-toc-title">TABLE OF CONTENTS</div>
              <ul>
                {toc.map(h => (
                  <li
                    key={h.id}
                    className={`toc-level-${h.level} ${activeId === h.id ? 'active' : ''}`}
                  >
                    <a href={`#${h.id}`}>{h.text}</a>
                  </li>
                ))}
              </ul>
            </aside>
          )}

          <article ref={articleRef} className={`blog-post-article ${toc.length === 0 ? 'no-toc' : ''}`}>
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
              {post.content_md || ''}
            </ReactMarkdown>
          </article>
        </div>
      </div>

      <Footer />
    </div>
  );
}
